/**
 * ⛓ LOCKSTEP — CASE FOLDER CONTRACT
 * Authority: ai-docs/case-workspace-guide.md
 * This file is a BOUND MEMBER of the folder-contract lockstep set. Changing the
 * folder set, tiers, categories or naming HERE obliges every other member in the
 * SAME COMMIT — SEED is the source, this is a projection (R1/R5).
 * Members: `grep -r "LOCKSTEP — CASE FOLDER CONTRACT"` + guide §6-ter (incl. the
 * uncommentable JSON/JSONL members).
 * Moving one member alone is the defect, even when every test passes: that is
 * exactly how this contract drifted for two months across five deploy axes.
 *
 * ⛓ CHANGING SOMETHING? guide §6-quater.2 is the CHANGE RECIPE — the ordered
 * checklist of every file to touch, on which deploy axis, in which order
 * (promote-first, roll-last: reversing serves a STALE contract until the
 * promote lands — and is FATAL only for a change that ADDS a lextools
 * export, D-40/D-42). §6-quater is the MAP of how it reaches a model.
 *
 * The `caseFolders.*` locale JSON takes no comment — its binding is declared here.
 */
/**
 * CaseNavigator case-mode — the real on-disk workspace file/folder tree
 * for the selected case, fetched via `case.workspace_tree` (hidden +
 * internal entries filtered server-side). Replaces the logical
 * zone+document-bucket view of the legacy `CaseWorkspaceNav`.
 *
 * Interaction model (workspace-file-tags expand-on-select):
 *   - single-click / tap on a COLLAPSED row SELECTS it — it expands in place to
 *     show the full description + action buttons (`data-active`/`aria-current`
 *     track `useWorkspaceTreeSelectionStore.selectedPath`).
 *   - a click on the ALREADY-EXPANDED card — anywhere on it — opens the
 *     CONTENT VIEWER (operator 2026-07-30; the viewer IS what a second click
 *     means). Details/edit is the PENCIL's job; the card's [X] collapses it.
 *     This replaced the 2026-07-03 model (expanded-click → deferred details
 *     dialog, double-click → viewer) and with it the whole
 *     click-timer disambiguation — a double-click now lands on the same
 *     viewer (`useWorkspaceFileStore.openFile`; `data-open` marks the row
 *     whose file is in the viewer). The viewer dispatches by extension — `.md`
 *     via AIEditor; other types fall back to the placeholder viewers.
 *
 *   SidePanel (home / cases mode)
 *     ├─ no case selected → <OverviewNav/>   (adaptive case overview)
 *     └─ case selected    → <CaseFileNav/>   (this — case file tree)
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Globe,
  Loader2,
  Pencil,
} from "lucide-react";
import {
  useActiveContextStore,
  useEffectiveScope,
} from "@/stores/useActiveContextStore";
import { useCaseWorkspaceStore } from "@/stores/useCaseWorkspaceStore";
import {
  useWorkspaceTreeSelectionStore,
  SELECTION_AUTO_COLLAPSE_MS,
} from "@/stores/useWorkspaceTreeSelectionStore";
import { useCaseWorkspaceTree } from "@/hooks/useCaseWorkspaceTree";
import { useCaseFolderMap } from "@/hooks/useCaseFolderMap";
import { useClients } from "@/hooks/useClients";
import { cn } from "@/lib/utils";
import { clientDisplayLabel } from "@/lib/client-shortname";
import { type CopyMoveSource } from "@/components/workspace/ResourceCopyMoveDialog";
import { FileDisplayDetailsDialog } from "@/components/shell/FileDisplayDetailsDialog";
import { RowContextMenu } from "@/components/shell/RowContextMenu";
import { CaseFilesSection } from "@/components/shell/CaseFilesSection";
import { classifyCaseError } from "@/lib/workspace/case-unreachable";
import {
  RAW_INTAKE_ZONE,
  SOURCES_ZONE,
  isCaseFilesOwnedZone,
} from "@/lib/workspace/case-files-view";
import {
  FileRow,
  directorySiblings,
  siblingNamesExcluding,
  isUserFacingFile,
  type SiblingEntry,
} from "@/components/shell/CaseFileRow";
import type {
  FolderMapEntry,
  WorkspaceTreeFile,
  WorkspaceTreeFolder,
} from "@/types/gateway";

/** Build a `Map<folderKey, FolderMapEntry>` for O(1) lookup keyed by
 *  the on-disk folder name. Memoized at the call site. */
function indexFolderMap(
  entries: readonly FolderMapEntry[] | undefined,
): Map<string, FolderMapEntry> {
  const out = new Map<string, FolderMapEntry>();
  if (!entries) return out;
  for (const e of entries) out.set(e.key, e);
  return out;
}

/** Hybrid label resolver per handoff §3:
 *    - `dynamic` → agent-provided inline `label[locale]` (fall through
 *      to any locale, then to the raw name).
 *    - `fixed`   → translate `caseFolders.<labelKey>` from the LexIDE
 *      i18n bundle; if the key is missing, fall back to the raw name.
 *    - unmapped  → raw on-disk name (graceful).
 */
function folderLabel(
  entry: FolderMapEntry | undefined,
  name: string,
  t: TFunction,
  locale: string,
): string {
  if (!entry) return name;
  // Keyed on HAVING an inline label map, never on a specific `kind`. Gating
  // this on `kind === "dynamic"` meant a `virtual` category entry — which
  // carries the same inline map — fell through to the raw on-disk key and
  // rendered its RAW KEY instead of the localized label, in every locale, with nothing
  // erroring. `kind` is a server-extensible vocabulary; a closed test against
  // it silently mis-renders every value added after this line was written.
  if (entry.label) {
    return entry.label[locale] ?? Object.values(entry.label)[0] ?? name;
  }
  if (entry.labelKey) {
    const key = `caseFolders.${entry.labelKey}`;
    const translated = t(key);
    return translated === key ? name : translated;
  }
  return name;
}

/**
 * Auto-collapse the expanded (selected) row — operator 2026-07-27.
 *
 * The expanded card is an ACTION surface, not a reading surface: it costs ~4x a
 * collapsed row, so it must not persist once the user's attention has moved. It
 * closes on any of:
 *   - **idle** for `SELECTION_AUTO_COLLAPSE_MS` (interaction with the card
 *     restarts the clock, so it is never yanked from someone mid-read);
 *   - a **pointerdown outside the tree** (another pane, the chat, the header);
 *   - a **route change** (navigating away).
 * Clicking another file re-selects (the old card closes as a consequence);
 * clicking a folder clears explicitly — see `FolderNode`.
 *
 * SUSPENDED while `overlayLocks > 0` — see the store for why that is required
 * rather than defensive.
 */
function useAutoCollapseSelection(navRef: React.RefObject<HTMLElement | null>) {
  const selectedPath = useWorkspaceTreeSelectionStore((s) => s.selectedPath);
  const overlayLocks = useWorkspaceTreeSelectionStore((s) => s.overlayLocks);
  const activityAt = useWorkspaceTreeSelectionStore((s) => s.activityAt);
  const clear = useWorkspaceTreeSelectionStore((s) => s.clear);
  const { pathname } = useLocation();

  // Idle timer. `activityAt` in the deps is what restarts it on interaction.
  useEffect(() => {
    if (!selectedPath || overlayLocks > 0) return;
    const id = window.setTimeout(clear, SELECTION_AUTO_COLLAPSE_MS);
    return () => window.clearTimeout(id);
  }, [selectedPath, overlayLocks, activityAt, clear]);

  // Pointerdown outside the tree.
  useEffect(() => {
    if (!selectedPath) return;
    const onPointerDown = (e: PointerEvent) => {
      // Read the CURRENT lock count — a portal overlay may have opened after
      // this listener was attached, and a click inside it lands "outside" the
      // tree DOM.
      if (useWorkspaceTreeSelectionStore.getState().overlayLocks > 0) return;
      const root = navRef.current;
      if (root && e.target instanceof Node && root.contains(e.target)) return;
      clear();
    };
    // Capture phase: fire even if something downstream stops propagation.
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [selectedPath, clear, navRef]);

  // Route change — but NOT on mount. A bare `useEffect(clear, [pathname])` also
  // fires on every MOUNT, so any remount of this pane (a workspace-tree refetch,
  // a StrictMode double-invoke in dev) silently wiped a selection the user had
  // just made. Compare against the previous value so only a real navigation
  // clears. (Caught in live testing: the row collapsed ~immediately after being
  // clicked.)
  const prevPathname = useRef(pathname);
  useEffect(() => {
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    clear();
  }, [pathname, clear]);
}

/** Sort siblings by `entry.order` (asc, default 500), then by name —
 *  same ordering rule the runtime uses on the wire (contract §4). */
function compareByMapOrder(
  a: WorkspaceTreeFolder,
  b: WorkspaceTreeFolder,
  map: Map<string, FolderMapEntry>,
): number {
  const ao = map.get(a.name)?.order ?? 500;
  const bo = map.get(b.name)?.order ?? 500;
  if (ao !== bo) return ao - bo;
  return a.name.localeCompare(b.name);
}

export interface CaseFileNavProps {
  /** When true, render as the TOP pane of a split SidePanel:
   *   - Hide the "Voltar" back button (the CaseNavigator below now
   *     provides the switch affordance — the ArrowLeft button would
   *     be redundant and takes vertical space).
   *   - Slightly tighter padding, matches the compact case-navigator
   *     row below it.
   *  Off = the historic full-pane rendering.
   *  Operator brief 2026-07-01. */
  compact?: boolean;
}

export function CaseFileNav({ compact = false }: CaseFileNavProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const scope = useEffectiveScope();
  // CCM Wave 2 — org-pane mode. When the effective scope is global
  // (no active case selection → the per-user "Global" workspace), this
  // SAME component renders the org workspace's file tree. The data hooks
  // (`useCaseWorkspaceTree`/`useCaseFolderMap`) already org-branch by
  // `useEffectiveScope`, so only the chrome (header + back affordance +
  // empty-state copy) differs here.
  const isOrg = scope === "org";
  const selection = useActiveContextStore((s) => s.selection);
  const caseData = useCaseWorkspaceStore((s) => s.data);
  const { data, isLoading, error } = useCaseWorkspaceTree();
  // ⚖ D-71 — is the case GONE, or did we merely fail to load it?
  const reach = classifyCaseError(error);

  // The active case's CLIENT, resolved for the discreet sub-title in the pane
  // header (operator 2026-07-06): the case is the prominent title, the client's
  // `@alias` handle (or a derived shortname) sits below it — critical context,
  // kept subordinate. Org scope has no client. `useClients` is already cached
  // (the CASOS navigator uses it).
  const { data: clients } = useClients();
  const activeClient =
    !isOrg && selection?.clientId
      ? clients?.find((c) => c.clientId === selection.clientId)
      : undefined;
  const activeClientName = activeClient?.name;
  const activeClientShort = clientDisplayLabel(activeClient, "");

  // Expand-on-select is per-case: clear the tree-row selection whenever the
  // active case (or the org scope) changes, so a stale highlight/expansion
  // never leaks across cases (mirrors the viewer pane's per-case reset).
  const clearTreeSelection = useWorkspaceTreeSelectionStore((s) => s.clear);
  const caseKey = isOrg ? "org" : (selection?.caseId ?? null);

  // ⚖ The heading must not assert a case that does not exist (ui-tester I-9,
  // run ui-20260903-2241). Creating a client with no case left this bar reading
  // "Caso sem título" — which tells the lawyer a case IS open and merely lacks
  // a name, directly above the client whose case list is empty.
  //
  // Same family as D-71 (`lib/workspace/case-unreachable.ts`): the failure that
  // matters is the CONFIDENT one. "Untitled" is a claim about a case; with no
  // caseId there is no case to make claims about, so we name the state instead.
  //
  // ⚠ `??` alone is not enough here — this wire spells absent as "" as well as
  // null, and an empty title would otherwise render as a blank heading rather
  // than falling through.
  const caseTitle = caseData?.title?.trim() ? caseData.title : null;
  // ⚠ "Untitled" is a POSITIVE assertion — the case loaded and genuinely has no
  // name. It must not double as the not-loaded-yet fallback, or every case open
  // flashes the very claim I-9 is about. Unknown gets its own word.
  const caseHeading = !selection?.caseId
    ? t("caseWorkspaceNav.noCase")
    : (caseTitle ?? (isLoading ? t("caseWorkspaceNav.loading") : t("caseWorkspaceNav.untitled")));
  useEffect(() => {
    clearTreeSelection();
  }, [caseKey, clearTreeSelection]);

  // The scope a copy/move SOURCES from — the active case (full triple) or
  // the per-user org workspace. `null` when a case is selected but its
  // parent ids aren't fully resolved (the action can't build a valid `from`
  // address, so it's suppressed on those rows). CCM Wave 3 (copy/move).
  const copyMoveSource = useMemo<CopyMoveSource | null>(() => {
    if (isOrg) return { scope: "org" };
    if (selection?.clientId && selection.matterId && selection.caseId) {
      return {
        scope: "case",
        clientId: selection.clientId,
        matterId: selection.matterId,
        caseId: selection.caseId,
      };
    }
    return null;
  }, [isOrg, selection]);
  // Presentation overlay: friendly labels + emoji + order + visibility
  // keyed by on-disk folder name. Renders even while the map fetch is
  // in flight — consumers fall back to the raw `folder.name` gracefully
  // (handoff §3) so the tree never goes blank waiting on the overlay.
  const folderMapQuery = useCaseFolderMap();
  const folderMap = useMemo(
    () => indexFolderMap(folderMapQuery.data?.folders),
    [folderMapQuery.data],
  );
  const locale = i18n.language;

  function handleBack() {
    useActiveContextStore.getState().clear();
    navigate("/workspace");
  }

  const root = data?.root;
  // §6.0 deploy-skew gate. The SPA and the runtime image ship on INDEPENDENT
  // axes, so a case's own tree cannot distinguish "this runtime predates the
  // ingest fields" from "these files are stale" — and reading the first as the
  // second would paint the lawyer's whole workspace with ⚠ warnings about
  // healthy files. The envelope marker is the honest discriminator; test its
  // PRESENCE, never its truthiness.
  const ingestMetaServed = data?.ingestMeta !== undefined;
  // Apply visibility + order overlays at the root before render so
  // hidden folders (e.g. `.lexiun`, `_archive`) never reach the
  // tree at all.
  const sourcesFolder = useMemo(
    () => root?.folders.find((f) => f.name === SOURCES_ZONE) ?? null,
    [root],
  );
  const rawIntakeFolder = useMemo(
    () => root?.folders.find((f) => f.name === RAW_INTAKE_ZONE) ?? null,
    [root],
  );
  /** The consolidated view replaces the ENTRADA/FONTES split — but only when
   *  the serving runtime actually provides the grouping data. */
  const showCaseFiles = ingestMetaServed && (sourcesFolder != null || rawIntakeFolder != null);

  const visibleRootFolders = useMemo(() => {
    if (!root) return [];
    return root.folders
      .filter((f) => folderMap.get(f.name)?.visible !== false)
      .filter((f) => !showCaseFiles || !isCaseFilesOwnedZone(f.name))
      .sort((a, b) => compareByMapOrder(a, b, folderMap));
  }, [root, folderMap, showCaseFiles]);
  const isEmpty =
    root != null &&
    visibleRootFolders.length === 0 &&
    root.files.length === 0 &&
    !showCaseFiles;
  // Compute the root directory's sibling set ONCE (not per child row).
  const rootSiblings = useMemo(() => (root ? directorySiblings(root) : []), [root]);

  // The expanded row collapses itself on idle / outside-click / navigation.
  const navRef = useRef<HTMLDivElement>(null);
  useAutoCollapseSelection(navRef);

  return (
    <div
      ref={navRef}
      className="flex h-full min-h-0 flex-col"
      data-testid={isOrg ? "org-workspace-nav" : "case-workspace-nav"}
      data-scope={isOrg ? "org" : "case"}
      data-compact={compact ? "true" : undefined}
    >
      {/* Header — a REVERSE-COLOUR title bar (matching the "CASOS" bar under the
          navigator): the case is the prominent title, with the client SHORTNAME
          on a discreet second line + the workflow status badge to the right
          (operator 2026-07-06). Org scope shows the "Workspace / General" label
          instead. PINNED — it lives OUTSIDE the scroll region below (as a
          `shrink-0` sibling), so it stays put while the file tree scrolls
          (operator 2026-07-06). Full-bleed: a direct child of the unpadded pane
          root, so it spans edge-to-edge with no negative margins. */}
      {/* Same heading family as `CaseNavigator`'s CASOS bar — light band in
          light mode since 2026-09-07 (operator), dark unchanged. */}
      <div className="shrink-0 bg-cream-100 px-2.5 py-1.5 text-coffee-900 dark:bg-coffee-700 dark:text-cream-50">
        <div className="flex items-center gap-1.5">
          {isOrg && <Globe size={14} className="shrink-0 opacity-80" aria-hidden />}
          <span
            className="flex-1 truncate text-sm font-semibold"
            data-testid={isOrg ? "org-workspace-nav-title" : "case-workspace-nav-title"}
            title={isOrg ? t("orgWorkspaceNav.title") : caseHeading}
          >
            {isOrg ? t("orgWorkspaceNav.title") : caseHeading}
          </span>
          {/* The case STATUS badge ("INTAKE") was removed here 2026-07-27
              (operator: "I am not sure we need this"). Three reasons it did not
              earn its place in THIS bar:
                1. it rendered the RAW English enum (`intake`) — there is no
                   `caseStatus` i18n block anywhere — so a pt-BR user read an
                   untranslated machine token;
                2. it is DUPLICATED by the work-area header, which already shows
                   an INTAKE badge two rows away;
                3. this bar's job is IDENTITY (which case, whose client).
                   Workflow state belongs with the work-area summary.
              The wider problem — case status rendered raw in ~5 lawyer-facing
              places — is tracked with the breadcrumb/summary redesign, which
              owns the surface where status legitimately belongs. */}
        </div>
        {/* Second line — the discreet client shortname (case scope) or the org
            subtitle. In case scope it's suppressed until the client resolves so
            the bar never shows a blank row. */}
        {isOrg ? (
          <span className="text-[10px] tracking-wide text-coffee-500 dark:text-cream-300">
            {t("orgWorkspaceNav.subtitle")}
          </span>
        ) : activeClientShort ? (
          <span
            className="block truncate text-[10px] tracking-wide text-coffee-500 dark:text-cream-300"
            title={activeClientName}
            data-testid="case-workspace-nav-client"
          >
            {activeClientShort}
          </span>
        ) : null}
      </div>

      {/* Scroll region — everything below the pinned header scrolls. `h-full` on
          the pane root (not `flex-1`) bounds it to the split pane's height so
          this `overflow-auto` actually scrolls; `flex-1 min-h-0` takes the
          height left under the fixed header. (h-full rationale: the parent
          `split-row-top` (SplitRow) has a definite flex-basis, so `flex-1` here
          would be a no-op and the tree would grow to content and never scroll —
          regression surfaced by the taller alias+description rows.) */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-auto",
          compact ? "p-2" : "p-3",
        )}
      >
        {/* The case "Back" affordance clears the selection + returns to the
            workspace overview. In org scope there's nothing to go "back"
            from (the org workspace IS the no-selection state), so it's
            suppressed there. In `compact` mode (the split-pane SidePanel)
            the CaseNavigator BELOW is the switch affordance, so this
            redundant back button is also hidden to save vertical space. */}
        {!isOrg && !compact && (
          <button
            type="button"
            onClick={handleBack}
            data-testid="case-workspace-nav-back"
            className="mb-2 flex items-center gap-1.5 self-start rounded-md px-1.5 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <ArrowLeft size={13} aria-hidden="true" />
            <span>{t("caseWorkspaceNav.back")}</span>
          </button>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
            <Loader2 size={14} className="animate-spin" />
            {isOrg ? t("orgWorkspaceNav.loading") : t("caseWorkspaceNav.loading")}
          </div>
        )}

        {error && (
          <div
            className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
            data-testid={
              isOrg
                ? "org-workspace-nav-error"
                : reach === "gone"
                  ? "case-workspace-nav-gone"
                  : reach === "retired"
                    ? "case-workspace-nav-retired"
                    : "case-workspace-nav-error"
            }
          >
            {/* ⚖ D-71: "gone" and "could not load" are different facts and the
                lawyer acts differently on each. ⚠ Only an explicit not-found
                code renders as gone — `classifyCaseError` falls back to
                RETRYABLE for anything it cannot classify, because "this case no
                longer exists" is the claim that cannot be walked back.
                ⚖ 2026-09-08: RETIRED is a THIRD fact, on the declared
                `CASE_RETIRED` code (`lexruntime-changes` `dba60048b`). It is
                deliberately NOT folded into `gone` — "este caso não existe
                mais" would be false of a case sitting intact in the lixeira,
                and the lawyer's next action differs: ask an admin to restore,
                rather than stop looking. Operator-surfaced via `ui-tester`. */}
            {isOrg
              ? t("orgWorkspaceNav.error")
              : reach === "gone"
                ? t("caseWorkspaceNav.gone")
                : reach === "retired"
                  ? t("caseWorkspaceNav.retired")
                  : t("caseWorkspaceNav.error")}
          </div>
        )}

        {/* Friendly empty/intro state. A fresh org workspace may be
            near-empty until the concierge writes to it — render an
            inviting intro, NOT an error. */}
        {isEmpty && isOrg && (
          <div
            data-testid="org-workspace-nav-empty"
            className="mt-2 rounded-lg border border-dashed border-cream-300/70 bg-cream-50/50 px-3 py-4 text-center dark:border-coffee-700/60 dark:bg-coffee-900/30"
          >
            <Globe size={20} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" aria-hidden />
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
              {t("orgWorkspaceNav.emptyTitle")}
            </div>
            <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              {t("orgWorkspaceNav.emptyBody")}
            </div>
          </div>
        )}

        {isEmpty && !isOrg && (
          <div className="px-1.5 py-2 text-[11px] text-slate-400 dark:text-slate-500">
            {t("caseWorkspaceNav.emptyZone")}
          </div>
        )}

        {root && !isEmpty && (
          <ul
            className="flex flex-col"
            data-testid="case-file-tree"
          >
            {showCaseFiles && (
              <li>
                <CaseFilesSection
                  sources={sourcesFolder}
                  rawIntake={rawIntakeFolder}
                  ingestMetaServed={ingestMetaServed}
                  caseKey={caseKey ?? "unknown"}
                  copyMoveSource={copyMoveSource}
                />
              </li>
            )}
            {visibleRootFolders.map((folder) => (
              <FolderNode
                key={folder.path}
                folder={folder}
                depth={0}
                folderMap={folderMap}
                locale={locale}
                copyMoveSource={copyMoveSource}
                siblings={rootSiblings}
              />
            ))}
            {root.files.filter(isUserFacingFile).map((file) => (
              <FileRow
                key={file.path}
                file={file}
                copyMoveSource={copyMoveSource}
                siblings={rootSiblings}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FolderNode({
  folder,
  depth,
  folderMap,
  locale,
  copyMoveSource,
  siblings,
}: {
  folder: WorkspaceTreeFolder;
  depth: number;
  folderMap: Map<string, FolderMapEntry>;
  locale: string;
  copyMoveSource: CopyMoveSource | null;
  siblings: readonly SiblingEntry[];
}) {
  const { t } = useTranslation();
  // Default-expand top-level folders so the tree opens to a useful
  // depth without a wall of collapsed rows; deeper folders start closed.
  const [expanded, setExpanded] = useState(depth === 0);
  // Clicking a FOLDER collapses any expanded file card (operator 2026-07-27:
  // "if user ... clicks on another file or folder"). A folder click is inside
  // the tree, so the outside-pointerdown path would not catch it, and toggling
  // a folder does not change `selectedPath` — hence the explicit clear.
  const clearSelection = useWorkspaceTreeSelectionStore((s) => s.clear);
  // Workspace-file-aliases (Part A): folder rename via the right-click menu.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);

  // Apply visibility + ordering to children at every depth — the map
  // entries are flat across the whole tree, keyed by basename, so the
  // same lookup works at any nesting level (contract §3).
  const visibleSubfolders = useMemo(
    () =>
      folder.folders
        .filter((f) => folderMap.get(f.name)?.visible !== false)
        .sort((a, b) => compareByMapOrder(a, b, folderMap)),
    [folder.folders, folderMap],
  );
  const childCount = visibleSubfolders.length + folder.files.length;
  // The sibling set for THIS folder's children — computed once per FolderNode,
  // not once per child row.
  const childSiblings = useMemo(() => directorySiblings(folder), [folder]);

  const entry = folderMap.get(folder.name);
  // Workspace-file-aliases (Part A): a user alias WINS over the folder-map
  // label (D2). Absent → the folder-map label → the raw name.
  const label = folder.alias?.trim()
    ? folder.alias.trim()
    : folderLabel(entry, folder.name, t, locale);
  const emoji = entry?.emoji;
  // Folder prominence (operator brief 2026-07-02): folders must read as
  // structure, not as just-another-row. Two tiers —
  //   depth 0 (Entrada / Fontes / …): a quiet uppercase CAPTION — no fill, no
  //     border. N3 retired the filled+bordered band: three chrome elements were
  //     competing with the content they framed. Structure now reads from letter-
  //     case + weight + the indent guide on the children (the VSCode model).
  //   depth ≥1: a plain 13px row, medium weight, one notch above the file rows.
  const isSection = depth === 0;

  return (
    <li
      data-testid="case-file-tree-folder"
      data-folder-path={folder.path}
      // Inter-section breathing room lives HERE (on the row wrapper), not on the
      // button — having both set a top margin stacked them.
      className={cn(isSection && "mt-2 first:mt-0")}
    >
      <button
        type="button"
        onClick={() => {
          clearSelection();
          setExpanded((e) => !e);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        aria-expanded={expanded}
        aria-label={expanded ? t("workspaceTree.collapseClient", { name: label }) : t("workspaceTree.expandClient", { name: label })}
        className={cn(
          "flex w-full items-center gap-1.5 rounded-md px-1.5 text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
          // N3: the depth-0 SECTION BAND (fill + border) is gone. A filled,
          // bordered header on every top-level folder was three chrome elements
          // competing with the content inside them. Structure now reads from the
          // uppercase caption + the indent guide, the way VSCode does it — which
          // is why its Explorer stays legible at far higher file counts.
          // ⚠ min-h, NOT h — a folder may carry a user description (a SECOND
          // line). A fixed height left that line overflowing the button
          // (measured: 33px of content in a 24px box). Single-line folders —
          // the overwhelming majority — still land on exactly 22/24px.
          isSection
            ? "min-h-[24px] hover:bg-slate-100 dark:hover:bg-slate-800"
            : "min-h-[22px] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
        )}
      >
        {expanded ? (
          <ChevronDown size={13} className="text-slate-400" />
        ) : (
          <ChevronRight size={13} className="text-slate-400" />
        )}
        {/* Prefer the map's emoji over the generic folder icon; fall back
            to lucide when no emoji is configured (unmapped folders or
            seed entries that don't specify one). */}
        {emoji ? (
          <span className="w-[14px] text-center text-[14px] leading-none" aria-hidden>
            {emoji}
          </span>
        ) : expanded ? (
          <FolderOpen size={14} className="text-slate-400 dark:text-slate-500" />
        ) : (
          <Folder size={14} className="text-slate-400 dark:text-slate-500" />
        )}
        <span
          className="flex min-w-0 flex-1 flex-col"
          // NEVER expose the OS folder name (plan §0) — tooltip = the full label.
          title={label}
        >
          {/* The NAME LINE — label + its count, on one baseline (operator
              2026-07-30). The count used to be a sibling of this whole column,
              which `flex-1` then shoved to the far right edge of the row; on an
              indented tree that put the number a long way from a short name like
              `files`, so the two read as unrelated. Binding it to the name is
              what the operator asked for, and it also survives truncation
              better — see `shrink-0` below.
              `items-baseline`, NOT `items-center`: this is small text beside
              larger text, so they should sit on a shared baseline rather than be
              centred against each other. */}
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span
              className={cn(
                "truncate",
                // N1: the gold folder family is GONE. Gold is the brand SIGNATURE
                // (active accent / focus ring / confirm glyph) — never paint, per
                // the restyle directives §0. The 2026-07-02 brief used it to make
                // folders read as structure; structure is now carried by WEIGHT +
                // position instead, which costs no colour budget.
                isSection
                  ? "text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                  : "text-[13px] font-medium text-slate-600 dark:text-slate-300",
                // Raw on-disk name (no alias, no folder-map entry) still
                // renders monospace (signals "not yet curated"); an alias or
                // a mapped label uses the body font for a friendlier look.
                folder.alias?.trim() || entry ? "" : "font-mono",
              )}
            >
              {label}
            </span>
            {/* N3 (still in force): a plain number, NOT a filled pill. A pill is
                a button-shaped affordance and this is a read-only count — the
                2026-07-30 change moved it, it did not re-style it.
                ⚠ `shrink-0` is load-bearing: without it the count is a
                shrinkable flex item and a long folder name squeezes it to
                nothing. The name ellipsizes; the number must not. */}
            {childCount > 0 && (
              <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                {childCount}
              </span>
            )}
          </span>
          {/* Workspace-file-aliases (Part A): the description note beneath the
              folder name. N1: dropped `font-light` + the /70 opacity — a diluted
              ink on a warm canvas reads muddy, so use the ramp step directly. */}
          {folder.description && (
            <span className="truncate text-[11px] text-slate-400 dark:text-slate-500">
              {folder.description}
            </span>
          )}
        </span>
      </button>

      {/* Right-click menu — folders currently offer Rename (alias/description). */}
      {menu && (
        <RowContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            {
              key: "rename",
              label: t("displayRename.renameAction"),
              Icon: Pencil,
              onSelect: () => setRenameOpen(true),
            },
          ]}
        />
      )}
      {renameOpen && (
        <FileDisplayDetailsDialog
          node={{
            path: folder.path,
            isDirectory: true,
            name: folder.name,
            alias: folder.alias,
            description: folder.description,
          }}
          siblingEffectiveNames={siblingNamesExcluding(siblings, folder.path)}
          onClose={() => setRenameOpen(false)}
        />
      )}

      {expanded && childCount > 0 && (
        <ul
          className={cn(
            // N3: ONE indent rule at every depth — 8px + a 1px guide (VSCode's
            // geometry). Previously depth-0 used a 13px offset + guide and
            // deeper levels a bare 16px indent, so nesting depth was ambiguous
            // exactly where it mattered. `gap-0.5` dropped: 22px rows already
            // separate cleanly, and the gap was inflating the list by ~9%.
            "ml-[7px] flex flex-col border-l border-cream-200/70 pl-2 dark:border-coffee-700/50",
          )}
        >
          {visibleSubfolders.map((sub) => (
            <FolderNode
              key={sub.path}
              folder={sub}
              depth={depth + 1}
              folderMap={folderMap}
              locale={locale}
              copyMoveSource={copyMoveSource}
              siblings={childSiblings}
            />
          ))}
          {folder.files.filter(isUserFacingFile).map((file) => (
            <FileRow
              key={file.path}
              file={file}
              copyMoveSource={copyMoveSource}
              siblings={childSiblings}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

